import { Component, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FilterHeaderComponent } from '../filter-header/filter-header.component';
import { BaseFilterComponent } from '../filter/base-filter.component';
import { AltitudeErrorStateMatcher } from './altitude-error-state-matcher';
import { LatitudeErrorStateMatcher } from './latitude-error-state-matcher';
import { LongitudeErrorStateMatcher } from './longitude-error-state-matcher';

@Component({
  selector: 'vectronic-coordinates-filter',
  templateUrl: './coordinates-filter.component.html',
  styleUrls: ['./coordinates-filter.component.scss'],
  imports: [FilterHeaderComponent, MatFormFieldModule, ReactiveFormsModule, MatInputModule],
})
export class CoordinatesFilterComponent extends BaseFilterComponent implements OnInit {
  typeOfSelection: string;
  latErrorStateMatcher: ErrorStateMatcher;
  longErrorStateMatcher: ErrorStateMatcher;
  altErrorStateMatcher: ErrorStateMatcher;

  lowestPossibleLatitudeValue = -90;
  highestPossibleLatitudeValue = 90;
  lowestPossibleLongitudeValue = -180;
  highestPossibleLongitudeValue = 180;
  lowestPossibleAltitudeValue = -99999;
  highestPossibleAltitudeValue = 99999;

  override ngOnInit() {
    super.ngOnInit();
    this.filtersForm().addValidators(this.latitudeErrorValidator as any);
    this.filtersForm().addValidators(this.longitudeErrorValidator as any);
    this.filtersForm().addValidators(this.altitudeErrorValidator as any);
    this.latErrorStateMatcher = new LatitudeErrorStateMatcher();
    this.longErrorStateMatcher = new LongitudeErrorStateMatcher();
    this.altErrorStateMatcher = new AltitudeErrorStateMatcher();
  }

  onEnableFilterToggled(checked: boolean) {
    this.filtersForm().get('coordinatesFilterEnabled').setValue(checked);
    this.isFilterHidden.set(!checked);
  }

  setValidCoordinatesFieldValue(fieldname: string, lowestPossibleValue, highestPossibleValue) {
    const field = this.filtersForm().get(fieldname);
    field.setValue(field.value.trim());

    for (let i = 10; i > 0 && field.invalid && field.value !== '-'; i--)
      field.setValue(field.value.substring(0, field.value.length - 1));

    if (!Number.isNaN(+field.value)) {
      if (field.value < lowestPossibleValue) field.setValue(lowestPossibleValue);
      if (field.value > highestPossibleValue) field.setValue(highestPossibleValue);
    }
  }

  latitudeErrorValidator(control: FormGroup): ValidationErrors | null {
    const minLat = control.get('minLatitude');
    const maxLat = control.get('maxLatitude');
    let latMinGreaterThenMax = false;

    if (maxLat?.value) {
      if (
        minLat.value &&
        (minLat.value + '').trim().length !== 0 &&
        (maxLat.value + '').trim().length !== 0 &&
        !Number.isNaN(+minLat.value) &&
        !Number.isNaN(+maxLat.value) &&
        +minLat.value > +maxLat.value
      ) {
        latMinGreaterThenMax = true;
      }

      if (maxLat.invalid && latMinGreaterThenMax)
        return { latMaxGeneralError: maxLat.invalid, latMinGreaterThenMax };

      if (maxLat.invalid) return { latMaxGeneralError: maxLat.invalid };
    }

    if (latMinGreaterThenMax) return { latMinGreaterThenMax };

    return null;
  }

  longitudeErrorValidator(control: FormGroup): ValidationErrors | null {
    const minLong = control.get('minLongitude');
    const maxLong = control.get('maxLongitude');
    let longMinGreaterThenMax = false;

    if (maxLong?.value) {
      if (
        minLong.value &&
        (minLong.value + '').trim().length !== 0 &&
        (maxLong.value + '').trim().length !== 0 &&
        !Number.isNaN(+minLong.value) &&
        !Number.isNaN(+maxLong.value) &&
        +minLong.value > +maxLong.value
      ) {
        longMinGreaterThenMax = true;
      }

      if (maxLong.invalid && longMinGreaterThenMax)
        return { longMaxGeneralError: maxLong.invalid, longMinGreaterThenMax };

      if (maxLong.invalid) return { longMaxGeneralError: maxLong.invalid };
    }

    if (longMinGreaterThenMax) return { longMinGreaterThenMax };

    return null;
  }

  altitudeErrorValidator(control: FormGroup): ValidationErrors | null {
    const minAlt = control.get('minAltitude');
    const maxAlt = control.get('maxAltitude');
    let altMinGreaterThenMax = false;

    if (maxAlt?.value) {
      if (
        minAlt.value &&
        maxAlt.value &&
        (minAlt.value + '').trim().length !== 0 &&
        (maxAlt.value + '').trim().length !== 0 &&
        !Number.isNaN(+minAlt.value) &&
        !Number.isNaN(+maxAlt.value) &&
        +minAlt.value > +maxAlt.value
      ) {
        altMinGreaterThenMax = true;
      }

      if (maxAlt.invalid && altMinGreaterThenMax)
        return { altMaxGeneralError: maxAlt.invalid, altMinGreaterThenMax };

      if (maxAlt.invalid) return { altMaxGeneralError: maxAlt.invalid };
    }

    if (altMinGreaterThenMax) return { altMinGreaterThenMax };

    return null;
  }
}
